import React, { useEffect, useMemo, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { API } from "../../data/API/API";
import { format } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrash, faXmark, faCheck } from "@fortawesome/free-solid-svg-icons";
import "./InternalCommentsModal.scss";

type Props = {
  show: boolean;
  onClose: () => void;

  supplierOrgNr: string;
  period: string; // yyyy-MM

  title?: string;

  // för att uppdatera ikon/badge i tabellen direkt efter create/update/delete
  onChanged?: () => void;
};

const NullReportingCommentsModal: React.FC<Props> = ({
  show,
  onClose,
  supplierOrgNr,
  period,
  title,
  onChanged,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [comments, setComments] = useState<any[]>([]);
  const [text, setText] = useState("");

  // Inline edit
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState("");

  // Busy state per row
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const headerTitle = useMemo(() => {
    if (title) return title;
    return `Interna kommentarer (Nollrapport) – ${supplierOrgNr} / ${period}`;
  }, [title, supplierOrgNr, period]);

  const fetchComments = async () => {
    setLoading(true);
    try {
      // Förväntat svar: [{ id, text, created, createdBy }, ...]
      const data = await API.NullReportingComments.Get(supplierOrgNr, period);
      setComments(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!show) return;

    // Reset när modalen öppnas
    setText("");
    setEditingId(null);
    setEditText("");
    setUpdatingId(null);
    setDeletingId(null);

    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, supplierOrgNr, period]);

  const handleCreate = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;

    setSaving(true);
    try {
      const created = await API.NullReportingComments.Create(supplierOrgNr, period, trimmed);
      if (created) {
        setComments((prev) => [created, ...prev]); // nyast först
        setText("");
        onChanged?.();
      }
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (commentId: number, currentText: string) => {
    setEditingId(commentId);
    setEditText(currentText);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const saveEdit = async (commentId: number) => {
    const trimmed = editText.trim();
    if (!trimmed) return;

    setUpdatingId(commentId);
    try {
      const updated = await API.NullReportingComments.Update(commentId, trimmed);
      if (updated) {
        setComments((prev) =>
          prev.map((c) => (c.id === commentId ? { ...c, text: updated.text } : c))
        );
        setEditingId(null);
        setEditText("");
        onChanged?.();
      }
    } finally {
      setUpdatingId(null);
    }
  };

  const remove = async (commentId: number) => {
    // Om du vill ha confirm: ersätt med window.confirm eller din modal-variant
    const ok = window.confirm("Ta bort kommentaren?");
    if (!ok) return;

    setDeletingId(commentId);
    try {
      const deleted = await API.NullReportingComments.Delete(commentId);
      if (deleted) {
        setComments((prev) => prev.filter((c) => c.id !== commentId));
        if (editingId === commentId) {
          setEditingId(null);
          setEditText("");
        }
        onChanged?.();
      }
    } finally {
      setDeletingId(null);
    }
  };

  const isBusy = (id: number) => updatingId === id || deletingId === id;

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton style={{backgroundColor:'#173c53',color:'white'}}>
        <Modal.Title>{headerTitle}</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{backgroundColor:'#fffaf5'}}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 12 }}>
            <Spinner animation="border" role="status" />
          </div>
        ) : (
          <>
            {/* Create */}
            <div style={{ marginBottom: 12 }}>
              <textarea
                className="form-control"
                rows={3}
                placeholder="Skriv en intern kommentar för leverantör & period..."
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={saving}
              />
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                <button className="btn" onClick={handleCreate} disabled={saving || !text.trim()}>
                  {saving ? "Sparar..." : "Spara kommentar"}
                </button>
              </div>
            </div>

            {/* List */}
            {comments.length === 0 ? (
              <p>Inga interna kommentarer ännu.</p>
            ) : (
              <div className="internal-comments-list">
                {comments.map((c) => {
                  const d = new Date(c.created);
                  const createdStr = isNaN(d.getTime()) ? c.created : format(d, "yyyy-MM-dd HH:mm");
                  const isEditing = editingId === c.id;

                  return (
                    <div key={c.id} className="internal-comment">
                      <div className="internal-comment-actions">
                        {isEditing ? (
                          <>
                            <button
                              className="icon-btn"
                              title="Avbryt"
                              onClick={cancelEdit}
                              disabled={isBusy(c.id)}
                            >
                              <FontAwesomeIcon icon={faXmark} />
                            </button>
                            <button
                              className="icon-btn"
                              title="Spara"
                              onClick={() => saveEdit(c.id)}
                              disabled={isBusy(c.id) || !editText.trim()}
                            >
                              <FontAwesomeIcon icon={faCheck} />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="icon-btn"
                              title="Redigera"
                              onClick={() => startEdit(c.id, c.text)}
                              disabled={isBusy(c.id)}
                            >
                              <FontAwesomeIcon icon={faPen} />
                            </button>
                            <button
                              className="icon-btn"
                              title="Ta bort"
                              onClick={() => remove(c.id)}
                              disabled={isBusy(c.id)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </>
                        )}
                      </div>

                      <div className="meta">
                        <b>{c.createdBy}</b>
                        <span className="date">{createdStr}</span>
                      </div>

                      {isEditing ? (
                        <textarea
                          className="form-control"
                          rows={3}
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          disabled={isBusy(c.id)}
                        />
                      ) : (
                        <div className="text">{c.text}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer style={{backgroundColor:'#fffaf5'}}>
        <button className="btn button-cancel" onClick={onClose}>
          Stäng
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default React.memo(NullReportingCommentsModal);
